const mentionTokenPattern = /<@(\d+)>/g;
const channelTokenPattern = /<!channel>/g;

export function userDisplayName(user) {
    return user?.name?.trim() || user?.username?.trim() || 'Unknown user';
}
export function conversationDisplayName(conversation, currentUserId) {
    if (!conversation) return 'Chat';
    if (conversation.kind === 'CHANNEL') return conversation.name || 'Unnamed channel';
    if (conversation.kind === 'GROUP_DM' && conversation.name?.trim()) return conversation.name;
    const otherNames = (conversation.members || [])
        .map(member => member.user)
        .filter(user => user.id !== currentUserId)
        .map(userDisplayName);
    return otherNames.join(', ') || 'Just you';
}

export function sortConversations(conversations, currentUserId) {
    const channels = conversations
        .filter(item => item.kind === 'CHANNEL')
        .toSorted((left, right) => conversationDisplayName(left, currentUserId)
            .localeCompare(conversationDisplayName(right, currentUserId)));
    const direct = conversations
        .filter(item => item.kind !== 'CHANNEL')
        .toSorted((left, right) => new Date(right.updated_at) - new Date(left.updated_at));
    return {channels, direct};
}

function conversationUnread(conversation) {
    return (conversation.unread_root_count || 0) + (conversation.unread_thread_count || 0);
}

function channelPriority(left, right, currentUserId) {
    const unreadDifference = Number(conversationUnread(right) > 0) - Number(conversationUnread(left) > 0);
    if (unreadDifference) return unreadDifference;
    const todayDifference = Number(Boolean(right.auto_channel?.has_job_today)) - Number(Boolean(left.auto_channel?.has_job_today));
    if (todayDifference) return todayDifference;
    if (conversationUnread(left) > 0 && conversationUnread(right) > 0) {
        const activityDifference = new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime();
        if (activityDifference) return activityDifference;
    }
    return conversationDisplayName(left, currentUserId).localeCompare(conversationDisplayName(right, currentUserId));
}

export function groupChannels(conversations, currentUserId, search = '') {
    const query = search.trim().toLocaleLowerCase();
    const channelMatches = conversation => {
        if (!query) return true;
        const automatic = conversation.auto_channel;
        const values = [
            conversationDisplayName(conversation, currentUserId),
            automatic?.agent_group?.label,
            automatic?.agent_group?.key,
            conversation.context?.label,
        ];
        return values.some(value => String(value || '').toLocaleLowerCase().includes(query));
    };
    const channels = conversations.filter(item => item.kind === 'CHANNEL' && channelMatches(item));
    const agentGroups = new Map();
    const workers = [];
    const other = [];
    channels.forEach(conversation => {
        if (conversation.auto_channel?.type === 'PROPERTY') {
            const key = conversation.auto_channel.agent_group?.key || 'unassigned';
            const label = conversation.auto_channel.agent_group?.label || 'Unassigned properties';
            const group = agentGroups.get(key) || {key, label, conversations: []};
            group.conversations.push(conversation);
            agentGroups.set(key, group);
        } else if (conversation.auto_channel?.type === 'WORKER') {
            workers.push(conversation);
        } else {
            other.push(conversation);
        }
    });
    const scoreGroup = group => ({
        unread: group.conversations.some(item => conversationUnread(item) > 0),
        today: group.conversations.some(item => item.auto_channel?.has_job_today),
    });
    const agents = [...agentGroups.values()].sort((left, right) => {
        const leftScore = scoreGroup(left);
        const rightScore = scoreGroup(right);
        if (leftScore.unread !== rightScore.unread) return leftScore.unread ? -1 : 1;
        if (leftScore.today !== rightScore.today) return leftScore.today ? -1 : 1;
        return left.label.localeCompare(right.label);
    });
    agents.forEach(group => group.conversations.sort((left, right) => channelPriority(left, right, currentUserId)));
    workers.sort((left, right) => channelPriority(left, right, currentUserId));
    other.sort((left, right) => channelPriority(left, right, currentUserId));
    return {agents, workers, other};
}

export function totalUnread(conversations) {
    return conversations.reduce((total, conversation) => total
        + (conversation.unread_root_count || 0)
        + (conversation.unread_thread_count || 0), 0);
}

export function encodeMentions(body, members, allowChannelMention) {
    let encoded = body;
    const users = (members || []).map(member => member.user || member);
    users.toSorted((left, right) => right.username.length - left.username.length).forEach(user => {
        const escaped = user.username.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        encoded = encoded.replace(new RegExp(`@${escaped}(?![A-Za-z0-9_.-])`, 'gi'), `<@${user.id}>`);
    });
    if (allowChannelMention) {
        encoded = encoded.replace(/@channel(?![A-Za-z0-9_.-])/gi, '<!channel>');
    }
    return encoded;
}

export function decodeMentions(body, members) {
    if (!body) return '';
    const users = new Map((members || []).map(member => {
        const user = member.user || member;
        return [String(user.id), user];
    }));
    return body
        .replace(mentionTokenPattern, (_, id) => `@${users.get(id)?.username || 'user'}`)
        .replace(channelTokenPattern, '@channel');
}

function preferNewerMessage(current, incoming) {
    if (!current) return incoming;
    const currentVersion = Number(current.version || 0);
    const incomingVersion = Number(incoming.version || 0);
    if (currentVersion > incomingVersion
        || (currentVersion === incomingVersion && current.deleted_at && !incoming.deleted_at)) {
        return current;
    }
    const currentQuote = current.quoted_message;
    const incomingQuote = incoming.quoted_message;
    if (currentQuote?.id && currentQuote.id === incomingQuote?.id) {
        const currentQuoteVersion = Number(currentQuote.version || 0);
        const incomingQuoteVersion = Number(incomingQuote.version || 0);
        if (currentQuoteVersion > incomingQuoteVersion
            || (currentQuoteVersion === incomingQuoteVersion && currentQuote.deleted && !incomingQuote.deleted)) {
            return {...incoming, quoted_message: currentQuote};
        }
    }
    return incoming;
}

export function reconcileMessagePage(current, incoming) {
    const currentById = new Map(current.map(message => [String(message.id), message]));
    return incoming.map(message => preferNewerMessage(currentById.get(String(message.id)), message));
}

export function mergeMessages(current, incoming) {
    const messages = new Map(current.map(message => [String(message.id), message]));
    incoming.forEach(message => messages.set(
        String(message.id),
        preferNewerMessage(messages.get(String(message.id)), message),
    ));
    return [...messages.values()].toSorted((left, right) => left.sequence - right.sequence);
}

export function messageEventId(event) {
    if (!['message.created', 'message.updated', 'message.deleted', 'reaction.added', 'reaction.removed', 'conversation.read'].includes(event?.type)) return 0;
    const messageId = Number(event?.data?.message_id || 0);
    return Number.isSafeInteger(messageId) && messageId > 0 ? messageId : 0;
}

export function messagesReferenceMessage(messages, messageId) {
    // Direct message lookup updates the changed row. Quote
    // preview is only needed for embedded quote cards, including cards whose
    // source row has fallen outside the latest message page.
    return (messages || []).some(message => Number(message.quoted_message?.id) === messageId);
}

export async function mapWithConcurrency(values, maximumConcurrent, mapper) {
    const results = new Array(values.length);
    let nextIndex = 0;
    const worker = async () => {
        while (nextIndex < values.length) {
            const index = nextIndex;
            nextIndex += 1;
            results[index] = await mapper(values[index], index);
        }
    };
    await Promise.all(Array.from(
        {length: Math.min(Math.max(1, maximumConcurrent), values.length)},
        () => worker(),
    ));
    return results;
}

export function applyQuotePreview(messages, quotedMessage, event) {
    if (!quotedMessage?.id) return messages;
    const version = Number(quotedMessage.version || event?.data?.version || 0);
    return messages.map(message => {
        let next = message;
        if (Number(message.id) === quotedMessage.id && (!version || Number(message.version || 0) <= version)) {
            next = quotedMessage.deleted ? {
                ...message,
                body: null,
                attachments: [],
                deleted_at: message.deleted_at || event?.occurred_at || new Date().toISOString(),
                ...(version > 0 ? {version} : {}),
            } : {
                ...message,
                body: quotedMessage.body ?? null,
                attachments: quotedMessage.attachments || [],
                sender: quotedMessage.sender || message.sender,
                edited_at: event?.type === 'message.updated'
                    ? event?.occurred_at || message.edited_at
                    : message.edited_at,
                ...(version > 0 ? {version} : {}),
            };
        }
        if (Number(message.quoted_message?.id) === quotedMessage.id
            && (!version || Number(message.quoted_message.version || 0) <= version)) {
            next = {...next, quoted_message: quotedMessage};
        }
        return next;
    });
}

export function groupedReactions(reactions, currentUserId) {
    const groups = new Map();
    (reactions || []).forEach(reaction => {
        const group = groups.get(reaction.emoji) || {emoji: reaction.emoji, count: 0, reacted: false};
        group.count += 1;
        if (reaction.user_id === currentUserId) group.reacted = true;
        groups.set(reaction.emoji, group);
    });
    return [...groups.values()];
}

export function chatErrorMessage(error) {
    if (!error) return null;
    const request = error.requestId ? ` (request ${error.requestId})` : '';
    return `${error.message || 'Chat request failed'}${request}`;
}
